import type { FC } from 'react';

import type { Offer } from '@psp-aggregator/sdk';

import { getOfferAttributes } from '../../libs/offerAttributes';
import { regionLabelToCode } from '../../libs/regions';

const FALLBACK = '#6366f1';

const STRIP_COLOR: Record<string, string> = {
  AFRICA: '#8b5cf6',
  APAC: '#14b8a6',
  ASIA: '#f59e0b',
  EEA: '#0ea5e9',
  EFTA: '#0ea5e9',
  EU: '#0ea5e9',
  GCC: '#d946ef',
  GLOBAL: FALLBACK,
  LATAM: '#10b981',
  MENA: '#f43f5e',
  'NORTH AMERICA': '#1d4ed8',
  SEPA: '#0ea5e9',
};

export interface CoverageStripProps {
  offer: Offer;
}

export const CoverageStrip: FC<CoverageStripProps> = ({ offer }) => {
  const regions = getOfferAttributes(offer).regions;
  const codes = regions.flatMap((region) => {
    const code = regionLabelToCode(region);

    return code ? [code] : [];
  });

  if (codes.length === 0 || codes.includes('GLOBAL') || codes.length > 3) {
    return <div className="h-1.5 w-full" style={{ background: FALLBACK }} />;
  }

  if (codes.length === 1) {
    return (
      <div
        className="h-1.5 w-full"
        style={{ background: STRIP_COLOR[codes[0]] ?? FALLBACK }}
      />
    );
  }

  const stops = codes
    .map((code, index) => {
      const start = (index / codes.length) * 100;
      const end = ((index + 1) / codes.length) * 100;

      return `${STRIP_COLOR[code] ?? FALLBACK} ${start}% ${end}%`;
    })
    .join(', ');

  return (
    <div
      className="h-1.5 w-full"
      style={{ background: `linear-gradient(to right, ${stops})` }}
    />
  );
};

CoverageStrip.displayName = 'CoverageStrip';
